import type { ButtonHTMLAttributes, ReactNode } from 'react'
import clsx from 'clsx'
import { MENU_OPTION_LIST_CLASS, COMPACT_POPOVER_MENU_ROW_CLASS } from './compactDropdownStyles'

interface PopoverMenuProps {
  children: ReactNode
  className?: string
}

interface PopoverMenuRowProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  icon?: ReactNode
  trailing?: ReactNode
  muted?: boolean
  tone?: 'neutral' | 'warning' | 'danger'
}

export const POPOVER_MENU_ICON_SIZE_PX = 16
export const POPOVER_MENU_ICON_SLOT_CLASS = 'flex h-4 w-4 flex-shrink-0 items-center justify-center'

export function PopoverMenu({ children, className }: PopoverMenuProps) {
  return <div className={clsx(MENU_OPTION_LIST_CLASS, className)}>{children}</div>
}

export function PopoverMenuDivider() {
  return <div className="my-1 border-t border-muted" />
}

export function PopoverMenuRow({
  children,
  icon,
  trailing,
  muted = false,
  tone = 'neutral',
  className,
  disabled,
  ...buttonProps
}: PopoverMenuRowProps) {
  const isDanger = tone === 'danger'
  const isWarning = tone === 'warning'

  return (
    <button
      type="button"
      disabled={disabled}
      className={clsx(
        COMPACT_POPOVER_MENU_ROW_CLASS,
        isDanger
          ? 'text-danger'
          : isWarning
            ? 'tone-amber text-[color:var(--tone-text)]'
            : muted
              ? 'text-muted'
              : 'text-subtle',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : isDanger
            ? 'hover:bg-[color:var(--surface-danger-subtle)]'
            : isWarning
              ? 'hover:bg-[color:var(--tone-bg-subtle)]'
              : 'hover:bg-[color:var(--bg-muted)]',
        trailing && 'justify-between',
        className
      )}
      {...buttonProps}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {icon && (
          <span
            className={clsx(
              POPOVER_MENU_ICON_SLOT_CLASS,
              isDanger ? 'text-danger' : isWarning ? 'text-[color:var(--tone-text)]' : 'text-muted'
            )}
          >
            {icon}
          </span>
        )}
        <span className="truncate">{children}</span>
      </span>
      {trailing}
    </button>
  )
}
